import * as React from 'react';
import { useState } from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Sheet from '@mui/joy/Sheet';
import CssBaseline from '@mui/joy/CssBaseline';
import Typography from '@mui/joy/Typography';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Link from '@mui/joy/Link';
import CircularProgress from '@mui/joy/CircularProgress';
import Alert from '@mui/joy/Alert';
import Auth from '../../utils/auth';

export default function RegisterComponent() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const orgFromQuery   = searchParams.get('org')?.trim() ?? '';
    const [formData, setFormData] = useState({ email: '', password: '', phone: '', workspace_name: orgFromQuery});

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        if (!validateEmail(formData.email)) {
            setError('Invalid email address. Please provide a valid email.');
            return;
        }

        setLoading(true);
        const { success, message, user } = await Auth.register(
            formData.email, 
            formData.password, 
            formData.phone,
            formData.workspace_name
        );

        if (success) {
            setSuccess('Registration successful! Redirecting...');
            setTimeout(() => navigate('/'), 2000); // Redirect to dashboard
        } else {
            setError(message);
        }

        setLoading(false);
    };

    return (
        <main>
            <CssBaseline />
            <Sheet
                sx={{
                    width: 400,
                    height: 'auto',
                    mx: 'auto',
                    my: 4,
                    py: 3,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    border: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                }}
                variant="outlined"
            >
                <div>
                    <Typography
                        level="h4"
                        component="h1"
                        sx={{ marginBottom: '10px', color: 'rgba(41, 27, 59, 1)', fontSize: '1.4rem' }}
                    >
                        <b>Sign Up</b>
                    </Typography>
                </div>

                {error && <Alert color="danger" variant="soft">{error}</Alert>}
                {success && <Alert color="success" variant="soft">{success}</Alert>}

                <form onSubmit={handleSubmit}>
                    <FormControl sx={{ marginBottom: '20px' }}>
                        <FormLabel>Email Address</FormLabel>
                        <Input
                            name="email"
                            type="email"
                            placeholder="example@email.com"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </FormControl>

                    <FormControl sx={{ marginBottom: '20px' }}>
                        <FormLabel>Password</FormLabel>
                        <Input
                            name="password"
                            type="password"
                            placeholder="Password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                    </FormControl>

                    <FormControl sx={{ marginBottom: '20px' }}>
                        <FormLabel>Phone Number</FormLabel>
                        <Input
                            name="phone"
                            type="tel"
                            placeholder="Phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                        />
                    </FormControl>

                    <FormControl sx={{ marginBottom: '20px' }}>
                        <FormLabel>Organization Name</FormLabel>
                        <Input
                            name="workspace_name"
                            type="text"
                            placeholder="Your organization name"
                            value={formData.workspace_name}
                            onChange={handleChange}
                            required
                            disabled={!!orgFromQuery}
                        />
                    </FormControl>

                    <Button
                        sx={{ mt: 1, backgroundColor: 'rgba(87, 42, 241, 1)', marginBottom: '30px' }}
                        fullWidth
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size="sm" color="neutral" /> : 'Register Now'}
                    </Button>

                    <Typography
                        endDecorator={<Link href="/login" sx={{ color: 'rgba(87, 42, 241, 1)' }}>Log in</Link>}
                        sx={{ fontSize: 'sm', display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
                    >
                        Already have an account?
                    </Typography>
                </form>
            </Sheet>
        </main>
    );
}